import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import DeckleImage from '@/components/generals/deckle-image'
import { productTypeLabel } from '@/constants/content'

interface ProductCardProps {
    product: Product
    sizes: string
    className?: string
    priority?: boolean
    showTagline?: boolean
    headingLevel?: 'h2' | 'h3'
}

export const PRODUCT_FALLBACK_IMAGE = '/images/qogoz/detail-deckle.jpg'

const ProductCard: FC<ProductCardProps> = ({
    product,
    sizes,
    className,
    priority = false,
    showTagline = false,
    headingLevel: Heading = 'h3'
}): ReactNode => {
    return (
        <Link
            href={`/shop/${product.slug}`}
            className={cn('group block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-bark', className)}
        >
            <div className='relative'>
                <DeckleImage
                    src={product.image || PRODUCT_FALLBACK_IMAGE}
                    alt={product.imageAlt || product.name}
                    sizes={sizes}
                    priority={priority}
                    imageClassName='transition-transform duration-[1800ms] ease-paper group-hover:scale-[1.045]'
                />
                {!product.available && (
                    <span className='absolute left-4 top-4 rounded-full bg-paper px-3 py-1 text-sm font-medium text-bark'>
                        Sold out
                    </span>
                )}
            </div>

            <div className='mt-5 flex items-start justify-between gap-4'>
                <div className='min-w-0'>
                    <p className='text-sm text-bark-soft'>
                        {productTypeLabel(product.type)}
                    </p>
                    <Heading className='mt-1.5 font-heading text-[1.3rem] leading-tight text-bark transition-colors duration-500 group-hover:text-pomegranate sm:text-[1.45rem]'>
                        <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                    </Heading>
                    {showTagline && product.tagline && (
                        <p data-mdlfy-cms={`products:${product.id}:tagline`} className='mt-2 max-w-sm text-[0.95rem] leading-relaxed text-bark-soft'>
                            {product.tagline}
                        </p>
                    )}
                    {product.packLabel && (
                        <p className='mt-1.5 text-sm text-bark-soft'>
                            <span data-mdlfy-cms={`products:${product.id}:pack_label`}>{product.packLabel}</span>
                        </p>
                    )}
                </div>
                <p className='shrink-0 font-heading text-[1.2rem] text-pomegranate'>
                    $<span>{product.price}</span>
                </p>
            </div>
        </Link>
    )
}

export default ProductCard
